import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {QuestionOpenOption} from "../../../../types/admin";
import { v4 as uuidv4 } from "uuid";
import {ADMIN_LANG, AVAILABLE_LANGUAGES} from "../../../../settings";
import {changeQuestionModal} from "../../../../app/slices/modals/questionEditModal";
import {DragDropContext, Draggable, Droppable, DropResult} from "@hello-pangea/dnd";
import {Col, Form, Row} from "react-bootstrap";
import {GripVertical, PlusCircle, Trash} from "react-bootstrap-icons";
import React from "react";

type Props = {
    language: string;
    is_draft: boolean;
}

const QuestionEditOpen = ({ language, is_draft }: Props) => {
    const { text_options } = useAppSelector((state) => state.questionEditModal);
    const dispatch = useAppDispatch();

    const handleAddOption = () => {
        const newOption: QuestionOpenOption = {
            option_id: uuidv4(),
            key: uuidv4(),
            type: 'text',
            required: false,
            languages: AVAILABLE_LANGUAGES.map((lang) => ({
                language_code: lang,
                label: '',
            })),
        };
        dispatch(changeQuestionModal({ text_options: [...text_options, newOption] }));
    }

    const handleRemoveOption = (id: string) => {
        dispatch(changeQuestionModal({ text_options: text_options.filter((option) => option.option_id !== id) }));
    }

    const handleOptionLabelChange = (id: string, label: string) => {
        const updatedOptions = text_options.map((option) => {
            if (option.option_id === id) {
                return {
                    ...option,
                    languages: option.languages.map((languageOption) =>
                        languageOption.language_code === language
                            ? { ...languageOption, label }
                            : languageOption
                    ),
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({ text_options: updatedOptions }));
    }

    const handleRequiredChange = (id: string, value: boolean) => {
        const updatedOptions = text_options.map((option) => {
            if (option.option_id === id) {
                return {
                    ...option,
                    required: value,
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({ text_options: updatedOptions }));
    }

    const handleTypeChange = (id: string, value: string) => {
        const updatedOptions = text_options.map((option) => {
            if (option.option_id === id) {
                return {
                    ...option,
                    type: value,
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({ text_options: updatedOptions }));
    }

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const reorderedOptions = [...text_options];
        const [removed] = reorderedOptions.splice(result.source.index, 1);
        reorderedOptions.splice(result.destination.index, 0, removed);
        dispatch(changeQuestionModal({ text_options: reorderedOptions }));
    }

    return (
        <Col xs={12}>
            <h4 className='pb-2 pt-3'>
                {ADMIN_LANG.open_options}
                <PlusCircle
                    onClick={handleAddOption}
                    className={`ms-1 utility-icon ${is_draft ? "green" : "disabled"}`}
                />
            </h4>
            <Row>
                <Col xs={6}>
                    <Form.Label>
                        {`${ADMIN_LANG.title} (${language.toUpperCase()})`}
                    </Form.Label>
                </Col>
                <Col xs={2}>
                    <Form.Label>
                        {ADMIN_LANG.type}
                    </Form.Label>
                </Col>
            </Row>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <Row
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className={`gy-2 ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                        >
                            {text_options.map((option, index) => {
                                const languageOption = option.languages.find(
                                    (lang) => lang.language_code === language
                                );

                                return (
                                    <Draggable isDragDisabled={!is_draft} key={option.key} draggableId={option.key} index={index}>
                                        {(provided, snapshot) => (
                                            <Col
                                                xs={12}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`${snapshot.isDragging ? 'dragging' : ''}`}
                                            >
                                                <Row className='row'>
                                                    <Col xs={6}>
                                                        <Form.Group controlId={`option_${option.key}_${language}`}>
                                                            <Form.Control
                                                                maxLength={255}
                                                                type='text'
                                                                className={`form-control ${!is_draft ? 'disabled' : ''}`}
                                                                value={languageOption?.label || ''}
                                                                onChange={(e) =>
                                                                        handleOptionLabelChange(option.option_id, e.target.value)
                                                                }
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs={2}>
                                                        <Form.Group controlId={`type_${option.key}`}>
                                                            <Form.Select value={option.type}
                                                                onChange={(e) => handleTypeChange(option.option_id, e.target.value)}>
                                                                <option value='text'>{ADMIN_LANG.text}</option>
                                                                <option value='textarea'>{ADMIN_LANG.textarea}</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs={2} className='d-flex justify-content-center align-items-center'>
                                                        <Form.Group controlId={`required_${option.key}_${language}`}>
                                                            <Form.Switch
                                                                label={ADMIN_LANG.field_required}
                                                                checked={option.required}
                                                                className='form-label'
                                                                onChange={(e) => handleRequiredChange(option.option_id, e.target.checked)}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs={2} className='d-flex justify-content-center align-items-center'>
                                                        <GripVertical
                                                            className={`utility-icon drag-icon ${!is_draft ? 'disabled' : ''}`}
                                                        />
                                                        <Trash
                                                            onClick={() => handleRemoveOption(option.option_id)}
                                                            className={`utility-icon ${!is_draft ? 'disabled' : 'red'}`}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </Row>
                    )}
                </Droppable>
            </DragDropContext>
        </Col>
    )
}

export default QuestionEditOpen;